import type { ItemFieldRow } from './item-field-reader';
import type { FormulaReadDependencies } from './formula-read-context';
import { createFormulaReadContext } from './formula-read-context';
import { readInventoryEquippedToken, readInventoryField, readInventoryRows, readItemUuidSlot } from './inventory-raw-readers';
import { readItemScalarLayerSlot } from './layer-cell-readers';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';
import { integerCompileWorkingRow, integerCompileResult, putIntegerCompilePath } from './integer-compute-slots';
import { appendPhpCompileArray } from './status-row-construction';
import { phpStableSort } from './php-stable-sort';
import { isPairedLimb, bodyPairsLimbs } from '../../shared/paired-limb-forms';

type Row = Record<string, unknown>;
const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const own = (v: unknown, k: string): unknown => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, k) ? (v as Row)[k] : undefined;
const path = (v: unknown, ...keys: string[]): unknown => keys.reduce(own, v);
const upper = (v: string): string => v.replace(/[a-z]/g, c => c.toUpperCase());
const valid = (v: string): boolean => /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(v);
const trim = (v: string): string => v.replace(/^[ \t\n\r\0\v]+|[ \t\n\r\0\v]+$/g, '');
export interface CreatureEquippedCountsDependencies extends FormulaReadDependencies {
    equipmentSlots: Readonly<Record<'main_hand' | 'off_hand' | 'both_hands' | 'head' | 'armor', string>>;
    naturalArmorPriority: readonly string[];
    shieldArmorType: string;
    /** Retained in initial-compile metadata; pairing uses the shared isPairedLimb reader. */
    pairedLimbForms: readonly string[];
    /** Also excludes the marker from natural-weapon deduplication. */
    engineFistMarkerId: string;
    quadrupedLayer: string;
    queryFullItem: (id: string) => ItemFieldRow | null;
    /** Borrow the page's canonical held inventory list on a delta. Initial
     * compiles retain their immutable layer-reader/copy contract. */
    inventoryRows?: readonly ItemFieldRow[];
}
/** Only equipment outputs; unrelated actor branches are never copied or normalized. */
export interface CreatureEquippedCluster {
    flags: Row;
    naturalArmor?: unknown[];
    naturalWeapon?: unknown[];
}

/** A delta witness for this member's directly equipped rows only. Contained,
 * deleted and absorbed rows cannot occupy its slots. No actor state is copied. */
export function equippedInventorySignature(host: ItemFieldRow): string {
    return JSON.stringify(readInventoryRows(host, 'inventory').flatMap(row => {
        const slot = readInventoryEquippedToken(row, host);
        return slot ? [[upper(phpJsonString(readInventoryField(row, 'inventory_id'))), upper(slot)]] : [];
    }));
}

// A reader overlay owns only the curated layer cells. Delegated descriptors never
// enumerate/copy actor values, including frozen initial-compile inputs.
function equipmentReadOverlay(source: unknown, changes: Row): Row {
    return new Proxy({}, {
        get: (_target, key) => Object.prototype.hasOwnProperty.call(changes, key) ? changes[String(key)] : own(source, String(key)),
        ownKeys: () => [...new Set([...Object.keys(arrayLike(source) ? source : {}), ...Object.keys(changes)])],
        getOwnPropertyDescriptor: (_target, key) => Object.prototype.hasOwnProperty.call(changes, key) || arrayLike(source) && Object.prototype.hasOwnProperty.call(source, key)
            ? { enumerable: true, configurable: true } : undefined,
    });
}

/** Read the same definition/instance fields as the possessor's fat instance,
 * borrowing their cells instead of cloning inventory rows on an equip delta. */
export function equipmentInventoryReadView(thin: ItemFieldRow, definition: ItemFieldRow, abilityTypes: readonly string[]): ItemFieldRow {
    const record = (value: unknown): Row => arrayLike(value) && !Array.isArray(value) ? value as Row : {};
    const changes: Row = {
        items: equipmentReadOverlay(record(own(definition, 'items')), record(own(thin, 'items'))),
        item_layer_types: record(own(definition, 'item_layer_types')),
    };
    if (arrayLike(own(definition, 'catalog'))) changes.catalog = own(definition, 'catalog');
    for (const name of abilityTypes) if (own(definition, name) !== undefined && own(thin, name) === undefined) changes[name] = own(definition, name);
    return equipmentReadOverlay(thin, changes);
}

/** The shared equipment rule. Initial compilation and held equipment deltas use
 * the same calculation; curation is applied only by the immutable initial API.
 */
export function createCreatureEquippedClusterCompiler(deps: CreatureEquippedCountsDependencies): (input: ItemFieldRow) => CreatureEquippedCluster | null {
    const readers = createFormulaReadContext(deps), slots = deps.equipmentSlots;
    const query = (id: string): ItemFieldRow | null => { deps.trace?.(['full-item', id]); return deps.queryFullItem(id); };
    const hasLayer = (row: ItemFieldRow, key: string): boolean => readers.layerValue(row, key) !== null || phpJsonTruthy(readers.layerEntries(row, key));
    return host => {
        if (!arrayLike(host)) throw new TypeError('Equipment recount requires a PHP array');
        if (!phpJsonTruthy(path(host, 'encounter_creatures', 'encounter_creature_id'))) return null;
        const cluster: CreatureEquippedCluster = { flags: {} };
        const counts: Record<string, number> = Object.fromEntries(['main_hand_equipped', 'off_hand_equipped', 'both_hands_equipped',
            'main_hand_weapon_equipped', 'off_hand_weapon_equipped', 'head_weapon_equipped', 'both_hands_weapon_equipped', 'armor_equipped',
            'shield_equipped', 'main_hand_natural_weapon_equipped', 'off_hand_natural_weapon_equipped'].map(key => [key, 0]));
        const equipped: Row = Object.fromEntries(['armor_inventory_id', 'armor_inventory_item_id', 'shield_inventory_id', 'shield_inventory_item_id',
            'main_hand_natural_weapon_item_id', 'off_hand_natural_weapon_item_id'].map(key => [key, '']));
        const inventoryRows = deps.inventoryRows ?? (hasLayer(host, 'inventory') && arrayLike(own(host, 'inventory'))
            ? Object.values(readers.layerEntries(host, 'inventory')) : []);
        for (const raw of inventoryRows) {
            const row = raw as ItemFieldRow | null;
            if (phpJsonInt(readInventoryField(row, 'inventory_private')) === 2) continue;
            const type = readers.itemField(row, 'item_type') ?? '', weapon = type === 'weapon';
            const slot = readInventoryEquippedToken(row, host);
            switch (slot) {
                case slots.main_hand: counts.main_hand_equipped = 1; if (weapon) counts.main_hand_weapon_equipped = 1; break;
                case slots.off_hand: counts.off_hand_equipped = 1; if (weapon) counts.off_hand_weapon_equipped = 1; break;
                case slots.head: if (weapon) counts.head_weapon_equipped = 1; break;
                case slots.both_hands: counts.main_hand_equipped = counts.off_hand_equipped = counts.both_hands_equipped = 1; if (weapon) counts.both_hands_weapon_equipped = 1; break;
                case slots.armor:
                    if (type === 'armor') { counts.armor_equipped = 1; equipped.armor_inventory_id = readInventoryField(row, 'inventory_id'); equipped.armor_inventory_item_id = readInventoryField(row, 'inventory_item_id'); }
                    break;
            }
            if ([slots.main_hand, slots.off_hand].includes(slot) && type === 'armor' && readItemUuidSlot(readers.layerValue(row, 'armor_type') ?? '') === deps.shieldArmorType) {
                counts.shield_equipped = 1; equipped.shield_inventory_id = readInventoryField(row, 'inventory_id'); equipped.shield_inventory_item_id = readInventoryField(row, 'inventory_item_id');
            }
        }
        const armors = hasLayer(host, 'natural_armor') ? Object.values(readers.layerEntries(host, 'natural_armor')) : [];
        if (armors.length) {
            const rank = (value: unknown): number => deps.naturalArmorPriority.indexOf(arrayLike(value) ? readItemScalarLayerSlot(value) : trim(phpJsonString(value)));
            const sorted = phpStableSort(armors, (a, b) => rank(b) - rank(a));
            if (counts.armor_equipped! > 0) cluster.naturalArmor = [];
            else { equipped.armor_inventory_item_id = readItemScalarLayerSlot(sorted[0] ?? ''); counts.armor_equipped = 1; cluster.naturalArmor = sorted; }
        }
        const rawNaturals = hasLayer(host, 'natural_weapon') ? Object.values(readers.layerEntries(host, 'natural_weapon')) : [];
        const memo = new Map<string, string>();
        const slotOf = (id: string): string => {
            const key = upper(id);
            if (!memo.has(key)) memo.set(key, readItemUuidSlot(readers.layerValue(query(id), 'equipment_slot') ?? ''));
            return memo.get(key)!;
        };
        if (rawNaturals.length) {
            const kept: unknown[] = [], seen = new Set<string>();
            for (const value of rawNaturals) {
                const id = readItemScalarLayerSlot(value); if (id === '' || !valid(id)) continue;
                const slot = slotOf(id);
                if (counts.main_hand_equipped! > 0 && counts.off_hand_equipped! > 0 && [slots.main_hand, slots.off_hand].includes(slot)) continue;
                if (slot === slots.head) counts.head_weapon_equipped = 1;
                const key = upper(id); if (seen.has(key)) continue;
                seen.add(key); kept.push(value);
            }
            cluster.naturalWeapon = kept;
        }
        // creature_natural_weapon_entries reads the newly curated host, then
        // merges computed grants. Its deduplication is case-sensitive.
        let occupancy = readers.layerEntries(cluster.naturalWeapon ? equipmentReadOverlay(host, {
            item_layer_types: equipmentReadOverlay(own(host, 'item_layer_types'), { natural_weapon: cluster.naturalWeapon }),
        }) : host, 'natural_weapon');
        const derived = path(host, 'computed', 'creature', 'natural_weapon');
        if (arrayLike(derived) && Object.keys(derived).length) {
            const seen = new Set(Object.values(occupancy).map(readItemScalarLayerSlot).filter(id => id !== ''));
            for (const value of Object.values(derived)) {
                const id = readItemScalarLayerSlot(value); if (id !== '' && seen.has(id)) continue;
                if (id !== '') seen.add(id); occupancy = appendPhpCompileArray(occupancy, value);
            }
        }
        if (Object.keys(occupancy).length) {
            type Limb = {id: string; slot: string};
            let pool: Limb[] = [], both: Limb | null = null;
            const seen = new Set<string>();
            for (const value of Object.values(occupancy)) {
                const id = readItemScalarLayerSlot(value); if (id === '' || !valid(id)) continue;
                const slot = slotOf(id);
                if ([slots.main_hand, slots.off_hand].includes(slot)) {
                    const key = upper(id); if (key === deps.engineFistMarkerId || seen.has(key)) continue;
                    seen.add(key); pool.push({id, slot});
                } else if (slot === slots.both_hands && both === null) both = {id, slot};
            }
            const mainFree = counts.main_hand_equipped === 0, offFree = counts.off_hand_equipped === 0;
            if (both !== null && mainFree && offFree) { counts.main_hand_natural_weapon_equipped = 1; equipped.main_hand_natural_weapon_item_id = both.id; pool = []; }
            const take = (slot: string): Limb | null => { const index = pool.findIndex(row => row.slot === slot); return index < 0 ? null : pool.splice(index, 1)[0]!; };
            let main = mainFree ? take(slots.main_hand) : null, off = offFree ? take(slots.off_hand) : null;
            if (mainFree && main === null && pool.length) main = pool.shift()!;
            if (offFree && off === null && pool.length) off = pool.shift()!;
            if (mainFree && offFree && ((main === null) !== (off === null))) {
                const solo = (main ?? off)!, definition = query(solo.id);
                const paired = arrayLike(definition) && Object.keys(definition).length > 0
                    && isPairedLimb(readItemUuidSlot(readers.layerValue(definition, 'natural_weapon_form') ?? ''), phpJsonString(readers.itemField(definition, 'item_id') ?? ''))
                    && bodyPairsLimbs(phpJsonInt(readers.layerValue(host, deps.quadrupedLayer) ?? 0), readers.itemField(host, 'item_type'));
                main = solo; off = paired ? solo : null;
            }
            if (main !== null) { counts.main_hand_natural_weapon_equipped = 1; equipped.main_hand_natural_weapon_item_id = main.id; }
            if (off !== null) { counts.off_hand_natural_weapon_equipped = 1; equipped.off_hand_natural_weapon_item_id = off.id; }
        }
        for (const key of ['count_main_hand_equipped', 'count_off_hand_equipped', 'count_both_hands_equipped',
            'count_main_hand_weapon_equipped', 'count_off_hand_weapon_equipped', 'count_both_hands_weapon_equipped', 'count_head_weapon_equipped',
            'count_main_hand_natural_weapon_equipped', 'count_off_hand_natural_weapon_equipped',
            'equipped_main_hand_natural_weapon_item_id', 'equipped_off_hand_natural_weapon_item_id',
            'count_armor_equipped', 'count_shield_equipped', 'equipped_armor_inventory_id', 'equipped_armor_inventory_item_id',
            'equipped_shield_inventory_id', 'equipped_shield_inventory_item_id']) {
            cluster.flags[key] = key.startsWith('count_') ? counts[key.slice(6)] : equipped[key.slice(9)];
        }
        return cluster;
    };
}

/** Immutable initial/from-base API, including its numeric-key normalization. */
export function createCreatureEquippedCountsCompiler(deps: CreatureEquippedCountsDependencies): (input: ItemFieldRow) => ItemFieldRow {
    const calculate = createCreatureEquippedClusterCompiler(deps);
    return input => {
        const cluster = calculate(input);
        const host = integerCompileWorkingRow(input);
        if (!cluster) return integerCompileResult(host);
        if (cluster.naturalArmor) putIntegerCompilePath(host, ['item_layer_types', 'natural_armor'], cluster.naturalArmor);
        if (cluster.naturalWeapon) putIntegerCompilePath(host, ['item_layer_types', 'natural_weapon'], cluster.naturalWeapon);
        const out: Row = {}; let index = 0;
        for (const [key, value] of Object.entries(host)) {
            const integerKey = /^(?:0|-?[1-9]\d*)$/.test(key) && BigInt(key) >= -9223372036854775808n && BigInt(key) <= 9223372036854775807n;
            Object.defineProperty(out, integerKey ? String(index++) : key, { value, enumerable: true, configurable: true, writable: true });
        }
        for (const [key, value] of Object.entries(cluster.flags)) out[key] = [value];
        return integerCompileResult(out);
    };
}
